import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import {
  qrRedirectPathCarriesTenant,
  readQrRedirectPath,
} from "../lib/qr-redirect-path";

interface PublicRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Componente para rutas públicas que redirige a usuarios autenticados.
 * Solo redirige si el token es VÁLIDO (isValid=true), evitando el bucle
 * que ocurría con tokens expirados en localStorage.
 */
export const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  redirectTo = "/"
}) => {
  const {
    token,
    selectedSubscriptionId,
    selectedCompanyId,
    isValid,
    isValidating: _isValidating,
    isReady
  } = useAuth();
  const location = useLocation();

  // Mientras se valida el token por primera vez (carga inicial), mostrar loading
  if (token && !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-sm text-muted-foreground">Validando sesión...</p>
        </div>
      </div>
    );
  }

  // Token existe pero es inválido (ej: expirado) — dejamos renderizar el login
  // AuthContext ya limpió el token del estado, así que esto no debería alcanzarse
  // con token inválido, pero lo dejamos como safety net.
  if (token && !isValid) {
    return <>{children}</>;
  }

  // Token válido: deep-link QR (incluye query) antes de selectores
  if (token && isValid) {
    const qrRedirectPath = readQrRedirectPath();
    if (qrRedirectPath && qrRedirectPathCarriesTenant(qrRedirectPath)) {
      return <Navigate to={qrRedirectPath} replace />;
    }
    if (selectedSubscriptionId && selectedCompanyId) {
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }
    if (selectedSubscriptionId === "SYSTEM") {
      return (
        <Navigate to="/" state={{ from: location }} replace />
      );
    }
    if (selectedSubscriptionId && !selectedCompanyId) {
      return (
        <Navigate to="/select-company" state={{ from: location }} replace />
      );
    }
    return (
      <Navigate to="/select-subscription" state={{ from: location }} replace />
    );
  }

  // Sin token o token inválido: renderizar contenido público
  return <>{children}</>;
};
